import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import ImageUploadWithCrop from '@/components/ImageUploadWithCrop';
import { toast } from 'sonner';
import { Save, Loader2, Plus, X, Award, MapPin, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIALIZATIONS = [
  'Residential', 'Commercial', 'Luxury Homes', 'Investment Properties',
  'First-Time Buyers', 'Relocation', 'New Construction', 'Foreclosures'
];

const LANGUAGES = [
  'English', 'Spanish', 'Mandarin', 'French', 'German', 'Italian',
  'Portuguese', 'Japanese', 'Korean', 'Hindi', 'Arabic'
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

export default function AdminProfileEditor({ open, onClose, userId, userType }) {
  const [profile, setProfile] = useState({
    photo_url: '', email: '', phone: '', title: '', agency_name: '',
    company_name: '', service_type: '',
    years_of_experience: '', website: '',
    social_media: { facebook: '', instagram: '', linkedin: '', twitter: '', youtube: '' },
    external_reviews_url: '', bio: '',
    specializations: [], languages: [], certifications: [], service_areas: [],
    properties_sold: '', business_hours: '', pricing_info: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userName, setUserName] = useState('');
  const [newCertification, setNewCertification] = useState('');
  const [newServiceArea, setNewServiceArea] = useState({ city: '', state: 'NJ' });

  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };
  const isProvider = userType === 'provider';
  const endpoint = isProvider
    ? `${API}/admin/providers/${userId}/public-profile`
    : `${API}/admin/agents/${userId}/public-profile`;

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(endpoint, { headers });
      setUserName(res.data.name || res.data.email || '');
      const p = res.data.public_profile || {};
      setProfile({
        photo_url: p.photo_url || '', email: p.email || '', phone: p.phone || '',
        title: p.title || '', agency_name: p.agency_name || '',
        company_name: p.company_name || '', service_type: p.service_type || '',
        years_of_experience: p.years_of_experience || '', website: p.website || '',
        social_media: { facebook: '', instagram: '', linkedin: '', twitter: '', youtube: '', ...(p.social_media || {}) },
        external_reviews_url: p.external_reviews_url || '', bio: p.bio || '',
        specializations: p.specializations || [], languages: p.languages || [],
        certifications: p.certifications || [], service_areas: p.service_areas || [],
        properties_sold: p.properties_sold || '', business_hours: p.business_hours || '',
        pricing_info: p.pricing_info || '',
      });
    } catch { toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  }, [endpoint]);

  useEffect(() => { if (open && userId) fetchProfile(); }, [open, userId, fetchProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { ...profile };
      // Convert empty strings to null for integer fields
      data.years_of_experience = data.years_of_experience ? parseInt(data.years_of_experience) || null : null;
      data.properties_sold = data.properties_sold ? parseInt(data.properties_sold) || null : null;
      // Strip empty strings to null for optional fields
      for (const key of ['photo_url','email','phone','title','agency_name','company_name','service_type','website','external_reviews_url','bio','business_hours','pricing_info']) {
        if (data[key] === '') data[key] = null;
      }
      await axios.put(endpoint, data, { headers });
      toast.success('Public profile updated');
      onClose(true);
    } catch { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  const set = (field, value) => setProfile(p => ({ ...p, [field]: value }));
  const setSocial = (key, value) => setProfile(p => ({ ...p, social_media: { ...p.social_media, [key]: value } }));

  const toggleSpecialization = (spec) => {
    setProfile(p => ({
      ...p,
      specializations: p.specializations.includes(spec)
        ? p.specializations.filter(s => s !== spec) : [...p.specializations, spec]
    }));
  };

  const toggleLanguage = (lang) => {
    setProfile(p => ({
      ...p,
      languages: p.languages.includes(lang)
        ? p.languages.filter(l => l !== lang) : [...p.languages, lang]
    }));
  };

  const addCertification = () => {
    if (newCertification.trim()) {
      setProfile(p => ({ ...p, certifications: [...p.certifications, newCertification.trim()] }));
      setNewCertification('');
    }
  };

  const addServiceArea = () => {
    if (newServiceArea.city.trim() && newServiceArea.state) {
      const titleCity = newServiceArea.city.trim().replace(/\b\w/g, c => c.toUpperCase());
      setProfile(p => ({ ...p, service_areas: [...p.service_areas, { city: titleCity, state: newServiceArea.state }] }));
      setNewServiceArea({ city: '', state: 'NJ' });
    } else { toast.error('Please enter both city and state'); }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-xl font-bold text-[#1A4D2E]">
            Edit Public Profile — {userName}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-16"><Loader2 size={28} className="animate-spin mx-auto text-stone-400" /></div>
        ) : (
          <div className="px-6 pb-6 space-y-5">

            {/* Basic Information */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Basic Information</CardTitle>
                <CardDescription>Professional details and contact information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ImageUploadWithCrop
                  currentImage={profile.photo_url}
                  onImageChange={(imageData) => set('photo_url', imageData)}
                  shape="oval"
                  label="Profile Photo"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Email</Label>
                    <Input value={profile.email} onChange={e => set('email', e.target.value)} placeholder="Not provided" />
                  </div>
                  <div>
                    <Label>Phone Number</Label>
                    <Input value={profile.phone} onChange={e => set('phone', e.target.value)} placeholder="Not provided" />
                  </div>
                </div>

                {!isProvider && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Title</Label>
                      <Input value={profile.title} onChange={e => set('title', e.target.value)} placeholder="e.g., Real Estate Agent, Broker Associate" />
                    </div>
                    <div>
                      <Label>Agency / Brokerage Name</Label>
                      <Input value={profile.agency_name} onChange={e => set('agency_name', e.target.value)} placeholder="e.g., Keller Williams Realty" />
                    </div>
                  </div>
                )}

                {isProvider && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Company Name</Label>
                      <Input value={profile.company_name} onChange={e => set('company_name', e.target.value)} />
                    </div>
                    <div>
                      <Label>Service Type</Label>
                      <Input value={profile.service_type} onChange={e => set('service_type', e.target.value)} />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Years of Experience</Label>
                    <Input type="number" value={profile.years_of_experience} onChange={e => set('years_of_experience', e.target.value)} placeholder="e.g., 10" />
                  </div>
                  {!isProvider ? (
                    <div>
                      <Label>Properties Sold</Label>
                      <Input type="number" value={profile.properties_sold} onChange={e => set('properties_sold', e.target.value)} placeholder="e.g., 150" />
                    </div>
                  ) : (
                    <div>
                      <Label>Business Hours</Label>
                      <Input value={profile.business_hours} onChange={e => set('business_hours', e.target.value)} placeholder="e.g., Mon-Fri 9am-5pm" />
                    </div>
                  )}
                </div>

                <div>
                  <Label>Website</Label>
                  <Input value={profile.website} onChange={e => set('website', e.target.value)} placeholder="https://yourwebsite.com" />
                </div>

                {isProvider && (
                  <div>
                    <Label>Pricing Info</Label>
                    <Input value={profile.pricing_info} onChange={e => set('pricing_info', e.target.value)} />
                  </div>
                )}

                {/* Social Media */}
                <div className="space-y-2">
                  <Label>Social Media Links</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { key: 'facebook', prefix: 'facebook.com/' },
                      { key: 'instagram', prefix: 'instagram.com/' },
                      { key: 'linkedin', prefix: 'linkedin.com/in/' },
                      { key: 'twitter', prefix: 'twitter.com/' },
                    ].map(({ key, prefix }) => (
                      <div key={key} className="space-y-1">
                        <Label className="text-xs text-stone-600 capitalize">{key === 'twitter' ? 'Twitter (X)' : key}</Label>
                        <div className="flex items-center">
                          <span className="px-3 py-2 bg-stone-100 border border-r-0 border-stone-300 rounded-l-md text-sm text-stone-600">{prefix}</span>
                          <Input value={profile.social_media[key]} onChange={e => setSocial(key, e.target.value)} placeholder="username" className="rounded-l-none" />
                        </div>
                      </div>
                    ))}
                    <div className="space-y-1 md:col-span-2">
                      <Label className="text-xs text-stone-600">YouTube</Label>
                      <div className="flex items-center">
                        <span className="px-3 py-2 bg-stone-100 border border-r-0 border-stone-300 rounded-l-md text-sm text-stone-600">youtube.com/@</span>
                        <Input value={profile.social_media.youtube || ''} onChange={e => setSocial('youtube', e.target.value)} placeholder="channelname" className="rounded-l-none" />
                      </div>
                    </div>
                    <div className="space-y-1 md:col-span-2">
                      <Label className="text-xs text-stone-600">External Reviews</Label>
                      <Input value={profile.external_reviews_url} onChange={e => set('external_reviews_url', e.target.value)} placeholder="https://g.page/your-business/review or any review link" />
                      <p className="text-xs text-stone-400">Google Reviews, Yelp, Zillow, or any external review page URL</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* About */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">About</CardTitle>
                <CardDescription>Professional bio and expertise</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Professional Bio</Label>
                  <Textarea value={profile.bio} onChange={e => set('bio', e.target.value)} placeholder="Write a compelling professional bio..." rows={5} />
                  <p className="text-xs text-stone-500 mt-1">{(profile.bio || '').length}/1000 characters</p>
                </div>

                <div>
                  <Label>Specializations</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {SPECIALIZATIONS.map(spec => (
                      <button key={spec} onClick={() => toggleSpecialization(spec)} type="button"
                        className={`px-3 py-1 rounded-full text-sm transition-colors cursor-pointer ${
                          profile.specializations.includes(spec) ? 'bg-[#1A4D2E] text-white' : 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                        }`}>{spec}</button>
                    ))}
                  </div>
                </div>

                {!isProvider && (
                  <div>
                    <Label>Languages Spoken</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {LANGUAGES.map(lang => (
                        <button key={lang} onClick={() => toggleLanguage(lang)} type="button"
                          className={`px-3 py-1 rounded-full text-sm transition-colors cursor-pointer ${
                            profile.languages.includes(lang) ? 'bg-blue-600 text-white' : 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                          }`}>{lang}</button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label>Certifications & Awards</Label>
                  <div className="flex gap-2 mt-2">
                    <Input value={newCertification} onChange={e => setNewCertification(e.target.value)}
                      placeholder="e.g., ABR, CRS, GRI" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCertification())} />
                    <Button onClick={addCertification} variant="outline"><Plus size={16} /></Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {profile.certifications.map((cert, idx) => (
                      <div key={idx} className="flex items-center gap-1 bg-amber-100 text-amber-800 px-3 py-1 rounded-full">
                        <Award size={14} /><span className="text-sm">{cert}</span>
                        <button onClick={() => setProfile(p => ({ ...p, certifications: p.certifications.filter((_, i) => i !== idx) }))}><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Service Areas */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Service Areas</CardTitle>
                <CardDescription>Cities and states where services are provided</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input value={newServiceArea.city} onChange={e => setNewServiceArea({ ...newServiceArea, city: e.target.value })}
                    placeholder="City" className="w-full sm:flex-1" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addServiceArea())} />
                  <div className="flex gap-2">
                    <Select value={newServiceArea.state} onValueChange={val => setNewServiceArea({ ...newServiceArea, state: val })}>
                      <SelectTrigger className="w-24"><SelectValue placeholder="State" /></SelectTrigger>
                      <SelectContent>
                        {US_STATES.map(st => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button onClick={addServiceArea} variant="outline"><Plus size={16} /></Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.service_areas.map((area, idx) => (
                    <div key={idx} className="flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full">
                      <MapPin size={14} /><span className="text-sm">{area.city}, {area.state}</span>
                      <button onClick={() => setProfile(p => ({ ...p, service_areas: p.service_areas.filter((_, i) => i !== idx) }))} className="ml-1"><X size={14} /></button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Save */}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#1A4D2E] hover:bg-[#143D24] text-white">
                {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
                Save Profile
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
